import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Pause, Play, SkipBack, SkipForward, Volume2 } from 'lucide-react';
import { albums } from '../data/siteData';

const AudioPlayer = () => {
  const [activeAlbumId, setActiveAlbumId] = useState('static-oaths');
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const audioRef = useRef(null);

  const activeAlbum = useMemo(
    () => albums.find((album) => album.id === activeAlbumId) || albums[0],
    [activeAlbumId]
  );
  const tracks = activeAlbum.tracks;
  const activeTrack = tracks[currentTrack];
  const hasPlayableAudio = Boolean(activeTrack?.audioUrl);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (isPlaying && hasPlayableAudio) {
      audioRef.current?.play();
    } else {
      audioRef.current?.pause();
    }
  }, [isPlaying, currentTrack, hasPlayableAudio]);

  const selectAlbum = (albumId) => {
    setActiveAlbumId(albumId);
    setCurrentTrack(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!hasPlayableAudio) {
      window.open(activeTrack.spotifyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setIsPlaying(!isPlaying);
  };

  const playTrack = (index) => {
    setCurrentTrack(index);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(Boolean(tracks[index].audioUrl));
  };

  const nextTrack = () => {
    if (currentTrack < tracks.length - 1) {
      setCurrentTrack(currentTrack + 1);
    }
  };

  const prevTrack = () => {
    if (currentTrack > 0) {
      setCurrentTrack(currentTrack - 1);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const bounds = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - bounds.left) / bounds.width;
    audio.currentTime = percent * audio.duration;
  };

  return (
    <div className="audio-player" data-testid="audio-player">
      <audio ref={audioRef} src={hasPlayableAudio ? activeTrack.audioUrl : undefined} preload="metadata" />

      <div className="audio-album-tabs" data-testid="album-selector">
        {albums.map((album) => (
          <button
            key={album.id}
            type="button"
            onClick={() => selectAlbum(album.id)}
            className={`album-tab ${activeAlbumId === album.id ? 'active' : ''}`}
            data-testid={`album-tab-${album.id}`}
          >
            <span>{album.title}</span>
            <small>{album.type}</small>
          </button>
        ))}
      </div>

      <div className="audio-album-stage">
        <div className="audio-cover-shell">
          <img
            src={activeTrack?.cover || activeAlbum.cover}
            alt={`${activeAlbum.title} cover art`}
            className="audio-cover-image"
            data-testid="audio-current-cover"
          />
        </div>

        <div className="audio-player-main">
          <div className="audio-album-info">
            <p className="audio-kicker">{activeAlbum.label}</p>
            <h3 className="audio-album-title" data-testid="audio-album-title">{activeAlbum.title}</h3>
            <p className="audio-current-track" data-testid="audio-current-track">{activeTrack?.title}</p>
            <p className="audio-album-summary">{activeAlbum.summary}</p>
          </div>
      
          <div className="audio-controls">
            <button
              type="button"
              onClick={prevTrack}
              disabled={currentTrack === 0}
              className="audio-btn"
              aria-label="Previous track"
              data-testid="audio-previous-button"
            >
              <SkipBack className="h-6 w-6" />
            </button>
            
            <button
              type="button"
              onClick={togglePlay}
              className="audio-btn-play"
              aria-label={hasPlayableAudio ? 'Play or pause track' : 'Open track on Spotify'}
              data-testid="audio-play-button"
            >
              {hasPlayableAudio ? (
                isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />
              ) : (
                <ExternalLink className="h-8 w-8" />
              )}
            </button>
            
            <button
              type="button"
              onClick={nextTrack}
              disabled={currentTrack === tracks.length - 1}
              className="audio-btn"
              aria-label="Next track"
              data-testid="audio-next-button"
            >
              <SkipForward className="h-6 w-6" />
            </button>
          </div>

          <div className="audio-progress-wrapper">
            <span className="audio-time" data-testid="audio-current-time">{formatTime(currentTime)}</span>
            <button
              type="button"
              className={`audio-progress-bar ${!hasPlayableAudio ? 'disabled' : ''}`}
              onClick={handleSeek}
              aria-label="Seek track"
              data-testid="audio-progress-bar"
            >
              <span
                className="audio-progress-fill" 
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </button>
            <span className="audio-time" data-testid="audio-duration">{hasPlayableAudio ? formatTime(duration) : 'Spotify'}</span>
          </div>

          <div className="audio-volume-wrapper">
            <Volume2 className="h-5 w-5 text-electric-blue" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="audio-volume-slider"
              disabled={!hasPlayableAudio}
              aria-label="Volume"
              data-testid="audio-volume-slider"
            />
          </div>
        </div>
      </div>

      <div className="audio-tracklist">
        <h4 className="audio-tracklist-title">Track List</h4>
        {tracks.map((track, index) => (
          <div key={`${track.title}-${index}`} className={`audio-track-row ${currentTrack === index ? 'active' : ''}`}>
            <button
              type="button"
              onClick={() => playTrack(index)}
              className="audio-track-item"
              data-testid={`track-select-${activeAlbum.id}-${index + 1}`}
            >
              <span className="track-number">{String(index + 1).padStart(2, '0')}</span>
              <span className="track-title">{track.title}</span>
              {currentTrack === index && isPlaying && (
                <span className="track-playing">▶</span>
              )}
            </button>
            {track.spotifyUrl && (
              <a
                href={track.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="track-spotify-link"
                aria-label={`Open ${track.title} on Spotify`}
                data-testid={`track-spotify-${activeAlbum.id}-${index + 1}`}
              >
                Spotify
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AudioPlayer;
